import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/credenciales';
import './SolarRadiationGraph.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SolarRadiationGraph = () => {
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [cityData, setCityData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStates = async () => {
            const querySnapshot = await getDocs(collection(db, 'Estados'));
            const statesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            statesData.sort((a, b) => a.name.localeCompare(b.name));
            setStates(statesData);
        };

        fetchStates();
    }, []);

    useEffect(() => {
        const fetchCities = async () => {
            if (selectedState) {
                const querySnapshot = await getDocs(collection(db, 'Ciudades'));
                const citiesData = querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(city => city.stateId === selectedState);
                setCities(citiesData);
            }
        };

        fetchCities();
    }, [selectedState]);

    useEffect(() => {
        if (selectedCity) {
            const city = cities.find(city => city.id === selectedCity);
            setCityData(city);
        }
    }, [selectedCity, cities]);

    const handleStateChange = (event) => {
        setSelectedState(event.target.value);
        setSelectedCity('');
        setCityData(null);
    };

    const handleCityChange = (event) => {
        setSelectedCity(event.target.value);
    };

    const data = {
        labels: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
        datasets: [
            {
                /* label: cityData ? `Radiación Solar de ${cityData.city}` : "Radiación Solar",
                 */
                label: "Radiación Solar",
                data: cityData ? [
                    cityData.Enero,
                    cityData.Febrero,
                    cityData.Marzo,
                    cityData.Abril,
                    cityData.Mayo,
                    cityData.Junio,
                    cityData.Julio,
                    cityData.Agosto,
                    cityData.Septiembre,
                    cityData.Octubre,
                    cityData.Noviembre,
                    cityData.Diciembre
                ] : [],
                borderColor: "rgb(255, 200, 0)",
                backgroundColor: "rgb(255, 77, 0)",
                tension: 0.4,
            },
        ],
    };

    const totalDuration = 2500;
    const delayBetweenPoints = totalDuration / data.labels.length;
    const previousY = (ctx) => ctx.index === 0 ? ctx.chart.scales.y.getPixelForValue(100) : ctx.chart.getDatasetMeta(ctx.datasetIndex).data[ctx.index - 1].getProps(['y'], true).y;
    const animation = {
        x: {
            type: 'number',
            easing: 'linear',
            duration: delayBetweenPoints,
            from: NaN, // the point is initially skipped
            delay(ctx) {
                if (ctx.type !== 'data' || ctx.xStarted) {
                    return 0;
                }
                ctx.xStarted = true;
                return ctx.index * delayBetweenPoints;
            }
        },
        y: {
            type: 'number',
            easing: 'linear',
            duration: delayBetweenPoints,
            from: previousY,
            delay(ctx) {
                if (ctx.type !== 'data' || ctx.yStarted) {
                    return 0;
                }
                ctx.yStarted = true;
                return ctx.index * delayBetweenPoints;
            }
        }
    };

    const options = {
        animation,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    color: "#121111",
                },
            },
            title: {
                display: true,
                text: cityData ? `Radiación Solar de ${cityData.city}` : "Radiación Solar",
                font: {
                    size: 20,
                    weight: "bold",
                },
                color: "#121111",
            },
        },
        responsive: true,
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "hrs",
                    color: "#0B3D91",
                },
            },
            x: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Meses",
                    color: "#0B3D91",
                },
            },
        },
    };

    return (
        <div className="solar-radiation-graph-container">
            <div className="actions-graph">
                <h1>Gráfica de Radiación Solar</h1>
                <button className="btn-cancel-graph" title="Regresar" onClick={() => navigate('/home')}><FontAwesomeIcon icon={faReply} /></button>
            </div>
            <div className="select-container">
                <label>
                    Selecciona un estado: &nbsp;
                    <select value={selectedState} onChange={handleStateChange}>
                        <option value="">Selecciona un estado</option>
                        {states.map(state => (
                            <option key={state.id} value={state.id}>{state.name}</option>
                        ))}
                    </select>
                </label>
                {selectedState && (
                    <label>
                        Selecciona una ciudad: &nbsp;
                        <select value={selectedCity} onChange={handleCityChange}>
                            <option value="">Selecciona una ciudad</option>
                            {cities.map(city => (
                                <option key={city.id} value={city.id}>{city.city}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
            {cityData && (
                <>
                    <h2 className="promedio">Promedio Anual: {cityData.Promedio_Anual}</h2>
                    <Line data={data} options={options} />
                </>
            )}
        </div>
    );
};

export default SolarRadiationGraph;